import { prepend, reverse, empty as empty$1 } from '../../Chunk/dist/effect-Chunk.esm.js';
import { dual } from '../../Function/dist/effect-Function.esm.js';
import { toString, toJSON, NodeInspectSymbol } from '../../Inspectable/dist/effect-Inspectable.esm.js';
import { length as length$1, isEmpty as isEmpty$1, append, shift, empty } from '../../MutableList/dist/effect-MutableList.esm.js';
import { pipeArguments } from '../../Pipeable/dist/effect-Pipeable.esm.js';

/**
 * @since 2.0.0
 */
const TypeId = /*#__PURE__*/Symbol.for("effect/MutableQueue");

/**
 * @since 2.0.0
 * @category symbol
 */

/**
 * @since 2.0.0
 * @category symbol
 */
const EmptyMutableQueue = /*#__PURE__*/Symbol.for("effect/mutable/MutableQueue/Empty");

/**
 * @since 2.0.0
 * @category model
 */

/**
 * @since 2.0.0
 */

const MutableQueueProto = {
  [TypeId]: TypeId,
  [Symbol.iterator]() {
    return Array.from(this.queue)[Symbol.iterator]();
  },
  toString() {
    return toString(this.toJSON());
  },
  toJSON() {
    return {
      _id: "MutableQueue",
      values: Array.from(this).map(toJSON)
    };
  },
  [NodeInspectSymbol]() {
    return this.toJSON();
  },
  pipe() {
    return pipeArguments(this, arguments);
  }
};
const make = capacity => {
  const queue = Object.create(MutableQueueProto);
  queue.queue = empty();
  queue.capacity = capacity;
  return queue;
};

/**
 * Creates a new bounded `MutableQueue`.
 *
 * @since 2.0.0
 * @category constructors
 */
const bounded = capacity => make(capacity);

/**
 * Creates a new unbounded `MutableQueue`.
 *
 * @since 2.0.0
 * @category constructors
 */
const unbounded = () => make(undefined);

/**
 * Returns the current number of elements in the queue.
 *
 * @since 2.0.0
 * @category getters
 */
const length = self => length$1(self.queue);

/**
 * Returns `true` if the queue is empty, `false` otherwise.
 *
 * @since 2.0.0
 * @category getters
 */
const isEmpty = self => isEmpty$1(self.queue);

/**
 * Returns `true` if the queue is full, `false` otherwise.
 *
 * @since 2.0.0
 * @category getters
 */
const isFull = self => self.capacity === undefined ? false : length$1(self.queue) === self.capacity;

/**
 * The **maximum** number of elements that a queue can hold.
 *
 * **Note**: unbounded queues can still implement this interface with
 * `capacity = Infinity`.
 *
 * @since 2.0.0
 * @category getters
 */
const capacity = self => self.capacity === undefined ? Infinity : self.capacity;

/**
 * Offers an element to the queue.
 *
 * Returns whether the enqueue was successful or not.
 *
 * @since 2.0.0
 */
const offer = /*#__PURE__*/dual(2, (self, value) => {
  const queueLength = length$1(self.queue);
  if (self.capacity !== undefined && queueLength === self.capacity) {
    return false;
  }
  append(value)(self.queue);
  return true;
});

/**
 * Enqueues a collection of values into the queue.
 *
 * Returns a `Chunk` of the values that were **not** able to be enqueued.
 *
 * @since 2.0.0
 */
const offerAll = /*#__PURE__*/dual(2, (self, values) => {
  const iterator = values[Symbol.iterator]();
  let next;
  let remainder = empty$1();
  let offering = true;
  while (offering && (next = iterator.next()) && !next.done) {
    offering = offer(next.value)(self);
  }
  while (next != null && !next.done) {
    remainder = prepend(next.value)(remainder);
    next = iterator.next();
  }
  return reverse(remainder);
});

/**
 * Dequeues an element from the queue.
 *
 * Returns either an element from the queue, or the `def` param.
 *
 * **Note**: if there is no meaningful default for your type, you can always
 * use `poll(MutableQueue.EmptyMutableQueue)`.
 *
 * @since 2.0.0
 */
const poll = /*#__PURE__*/dual(2, (self, def) => {
  if (isEmpty$1(self.queue)) {
    return def;
  }
  return shift(self.queue);
});

/**
 * Dequeues up to `n` elements from the queue.
 *
 * Returns a `List` of up to `n` elements.
 *
 * @since 2.0.0
 */
const pollUpTo = /*#__PURE__*/dual(2, (self, n) => {
  let result = empty$1();
  let count = 0;
  while (count < n) {
    const element = poll(EmptyMutableQueue)(self);
    if (element === EmptyMutableQueue) {
      break;
    }
    result = prepend(element)(result);
    count += 1;
  }
  return reverse(result);
});

export { EmptyMutableQueue, bounded, capacity, isEmpty, isFull, length, offer, offerAll, poll, pollUpTo, unbounded };
